import type { Project } from '@stfors/shared-types';
import { partitionByClosed } from '../utils/project';

// The <option> list for every project picker in the app. Renders option /
// optgroup nodes only, so it drops straight into an existing <select> beneath
// whatever placeholder that form uses ("Select a project…", "All projects",
// "None", …).
//
// A closed case is grouped rather than hidden: the last invoice and the hotel
// receipt that posts a week late both arrive after a case closes, so removing
// it outright would force a re-open to file ordinary work. Under its own
// heading it's still reachable but plainly not part of the live caseload.
//
// The headings appear only when they carry information — a firm with no closed
// cases gets a flat list, exactly as before this component existed.
interface Props {
  projects: readonly Project[];
}

export function ProjectOptions({ projects }: Props): JSX.Element {
  const { active, closed } = partitionByClosed(projects);

  if (closed.length === 0) return <>{active.map(renderOption)}</>;
  if (active.length === 0) {
    return <optgroup label="Closed">{closed.map(renderOption)}</optgroup>;
  }

  return (
    <>
      <optgroup label="Active">{active.map(renderOption)}</optgroup>
      <optgroup label="Closed">{closed.map(renderOption)}</optgroup>
    </>
  );
}

function renderOption(p: Project): JSX.Element {
  return (
    <option key={p.id} value={p.id}>
      {p.case_number ? `${p.case_number} — ` : ''}
      {p.title}
    </option>
  );
}
